import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BASE = 'https://allcom.cl';
const SCHEMA_ID = 'allcom-page-schema';

// Inject JSON-LD schema into page head
const injectSchema = (schemas) => {
  // Remove previous page-level schema
  const existing = document.getElementById(SCHEMA_ID);
  if (existing) existing.remove();

  if (!schemas || schemas.length === 0) return;

  const script = document.createElement('script');
  script.id = SCHEMA_ID;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(schemas.length === 1 ? schemas[0] : schemas);
  document.head.appendChild(script);
};

// Build breadcrumb schema
const breadcrumb = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "name": item.name,
    "item": `${BASE}${item.path}`,
  })),
});

// Build FAQ schema
const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(f => ({
    "@type": "Question",
    "name": f.q,
    "acceptedAnswer": { "@type": "Answer", "text": f.a },
  })),
});

// Build Service schema
const serviceSchema = (name, desc, slug) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": name,
  "description": desc,
  "provider": { "@type": "Organization", "name": "ALLCOM S.A.", "url": BASE },
  "areaServed": { "@type": "Country", "name": "Chile" },
  "url": `${BASE}/servicios/${slug}`,
});

// Build Product schema (rich, with price + image when available)
const productSchema = (product) => {
  // Backward-compatible signature: (product) OR (name, desc, brand, slug, category)
  let p;
  if (typeof product === 'object' && product !== null && !Array.isArray(product)) {
    p = product;
  } else {
    // Legacy positional args: (name, desc, brand, slug, category)
    const [name, desc, brand, slug, category] = arguments;
    p = { name, description: desc, brand, slug, category };
  }

  const slug = p.slug || '';
  const category = p.category || '';
  const url = category
    ? `${BASE}/hardware/${category}/${slug}`
    : `${BASE}/productos/${slug}`;

  // Resolve image: array of {url} or strings, or legacy `image` string
  let image;
  if (Array.isArray(p.images) && p.images.length > 0) {
    const first = p.images[0];
    image = typeof first === 'string' ? first : first?.url;
  }
  if (!image && p.image) image = p.image;
  if (image && image.startsWith('/')) image = `${BASE}${image}`;

  // Resolve numeric price (skip placeholder 9999999)
  const rawPrice = p.price_display || p.price;
  const numericPrice = typeof rawPrice === 'string'
    ? parseInt(String(rawPrice).replace(/[^\d]/g, ''), 10)
    : (typeof rawPrice === 'number' ? rawPrice : null);
  const validPrice = numericPrice && numericPrice > 0 && numericPrice !== 9999999;

  const offers = {
    "@type": "Offer",
    "priceCurrency": "CLP",
    "availability": "https://schema.org/InStock",
    "url": url,
    "seller": { "@type": "Organization", "name": "ALLCOM S.A.", "url": BASE },
    "itemCondition": p.refurb ? "https://schema.org/RefurbishedCondition" : "https://schema.org/NewCondition",
  };
  if (validPrice) offers.price = numericPrice;

  const node = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": p.name,
    "description": p.description || p.summary || '',
    "brand": { "@type": "Brand", "name": p.brand || "ALLCOM" },
    "category": p.category_display || category,
    "url": url,
    "offers": offers,
  };
  if (image) node.image = image;
  if (p.sku || p.slug) node.sku = p.sku || p.slug;
  if (p.mpn) node.mpn = p.mpn;
  return node;
};

// Common FAQs used across solution/service pages
const commonFAQs = [
  { q: '¿Cuál es el tiempo de implementación típico?', a: 'Dependiendo de la escala, entre 2-8 semanas desde la firma. Para pilotos, 5-7 días hábiles.' },
  { q: '¿Ofrecen modelo de arriendo?', a: 'Sí, todos nuestros productos están disponibles en modelo OPEX (arriendo mensual) sin CAPEX inicial.' },
  { q: '¿Qué incluye el soporte 24/7?', a: 'Mesa de ayuda, técnicos en terreno, repuestos en stock, monitoreo remoto proactivo y reportes mensuales.' },
  { q: '¿Se integra con nuestro sistema actual?', a: 'Sí, realizamos integración con POS, ERP, CRM y sistemas de pago existentes.' },
  { q: '¿Puedo probar antes de comprar?', a: 'Absolutamente. Nuestro Programa Piloto de 30 días te permite probar con equipos reales en tu operación.' },
  { q: '¿Tienen cobertura nacional?', a: 'Sí, operamos en las principales ciudades de Chile con técnicos en terreno y logística nacional.' },
];

const pilotFAQs = [
  { q: '¿Qué incluye el piloto?', a: 'Equipos reales, instalación, configuración, integración básica, soporte dedicado y reporte ejecutivo de ROI.' },
  { q: '¿Cuánto dura?', a: '30 días calendario, con posibilidad de extensión según evaluación.' },
  { q: '¿Tiene costo?', a: 'El piloto tiene un costo reducido que se descuenta si se aprueba el proyecto.' },
  { q: '¿Qué pasa al terminar?', a: 'Recibes un reporte de ROI. Puedes comprar, arrendar o devolver sin compromiso.' },
];

const arriendoFAQs = [
  { q: '¿Cuál es el plazo mínimo?', a: 'El plazo mínimo de arriendo es de 12 meses, con opciones de 24 y 36 meses.' },
  { q: '¿Qué incluye la cuota?', a: 'Hardware, software básico, soporte técnico, seguro de equipos y renovación tecnológica.' },
  { q: '¿Sirve para licitaciones?', a: 'Sí, el modelo OPEX es ideal para sector público ya que no requiere inversión de capital.' },
  { q: '¿Puedo escalar?', a: 'Sí, puedes agregar o reducir equipos durante el contrato según necesidad.' },
];

// Schema data per page
const pageSchemas = {
  '/': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }]),
  ],
  '/soluciones': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Soluciones', path: '/soluciones' }]),
  ],
  '/soluciones/autoservicio-self-checkout': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Soluciones', path: '/soluciones' }, { name: 'Autoservicio & Self-Checkout', path: '/soluciones/autoservicio-self-checkout' }]),
    faqSchema(commonFAQs),
    serviceSchema('Autoservicio & Self-Checkout', 'Kioscos self-checkout integrados con POS, soporte 24/7 y arriendo sin CAPEX. Líder en autoservicio en Chile.', 'autoservicio-self-checkout'),
  ],
  '/soluciones/esl-precios-digitales': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Soluciones', path: '/soluciones' }, { name: 'ESL / Precios Digitales', path: '/soluciones/esl-precios-digitales' }]),
    faqSchema(commonFAQs),
    serviceSchema('ESL / Precios Digitales', 'Etiquetas electrónicas SOLUM con integración POS/ERP y actualización masiva de precios.', 'esl-precios-digitales'),
  ],
  '/soluciones/carteleria-digital': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Soluciones', path: '/soluciones' }, { name: 'Cartelería Digital', path: '/soluciones/carteleria-digital' }]),
    faqSchema(commonFAQs),
    serviceSchema('Cartelería Digital', 'Digital signage premium con gestión centralizada y analítica para retail.', 'carteleria-digital'),
  ],
  '/soluciones/gestion-filas': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Soluciones', path: '/soluciones' }, { name: 'Gestión de Filas', path: '/soluciones/gestion-filas' }]),
    faqSchema(commonFAQs),
    serviceSchema('Gestión de Filas', 'Sistema de turnos y filas inteligentes con kioscos y analytics en tiempo real.', 'gestion-filas'),
  ],
  '/soluciones/aeropuertos': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Soluciones', path: '/soluciones' }, { name: 'Aeropuertos', path: '/soluciones/aeropuertos' }]),
    faqSchema(commonFAQs),
    serviceSchema('Aeropuertos & Alto Tráfico', 'Estaciones de carga, medidores de equipaje y kioscos para aeropuertos.', 'aeropuertos'),
  ],
  '/soluciones/pesaje-industrial': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Soluciones', path: '/soluciones' }, { name: 'Pesaje Industrial', path: '/soluciones/pesaje-industrial' }]),
    faqSchema(commonFAQs),
    serviceSchema('Pesaje Industrial & Comercial', 'Balanzas industriales y comerciales Marques con integración ERP.', 'pesaje-industrial'),
  ],
  '/soluciones/portillones-automaticos': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Soluciones', path: '/soluciones' }, { name: 'Portillones Automáticos', path: '/soluciones/portillones-automaticos' }]),
    faqSchema(commonFAQs),
    serviceSchema('Portillones Automáticos', 'Control de acceso inteligente para supermercados y retail con sensores y QR.', 'portillones-automaticos'),
  ],
  '/hardware': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Hardware', path: '/hardware' }]),
  ],
  '/software': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Software', path: '/software' }]),
  ],
  '/servicios': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Servicios', path: '/servicios' }]),
  ],
  '/servicios/soporte-24-7': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Servicios', path: '/servicios' }, { name: 'Soporte 24/7', path: '/servicios/soporte-24-7' }]),
    faqSchema(commonFAQs),
    serviceSchema('Soporte Técnico 24/7', 'Mesa de ayuda 24/7, técnicos en terreno, SLA garantizado y monitoreo remoto.', 'soporte-24-7'),
  ],
  '/servicios/piloto': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Servicios', path: '/servicios' }, { name: 'Programa Piloto', path: '/servicios/piloto' }]),
    faqSchema(pilotFAQs),
    serviceSchema('Programa Piloto 30 Días', '30 días con equipos reales, soporte completo y reporte ROI. Sin compromiso.', 'piloto'),
  ],
  '/servicios/arriendo-leasing': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Servicios', path: '/servicios' }, { name: 'Arriendo & Leasing', path: '/servicios/arriendo-leasing' }]),
    faqSchema(arriendoFAQs),
    serviceSchema('Arriendo & Leasing Tecnológico', 'Hardware + software + operación en cuota mensual. Cero CAPEX inicial.', 'arriendo-leasing'),
  ],
  '/servicios/implementacion-obras': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Servicios', path: '/servicios' }, { name: 'Implementación', path: '/servicios/implementacion-obras' }]),
    serviceSchema('Implementación & Obras', 'Obras civiles, eléctricas, red y montaje llave en mano para retail.', 'implementacion-obras'),
  ],
  '/servicios/renovaciones-tecnologicas': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Servicios', path: '/servicios' }, { name: 'Renovaciones Tecnológicas', path: '/servicios/renovaciones-tecnologicas' }]),
    serviceSchema('Renovaciones Tecnológicas', 'Renovación masiva de equipamiento con staging, logística y cartas Gantt.', 'renovaciones-tecnologicas'),
  ],
  '/servicios/obras-civiles': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Servicios', path: '/servicios' }, { name: 'Obras Civiles', path: '/servicios/obras-civiles' }]),
    serviceSchema('Obras Civiles', 'Adaptación de espacios comerciales para integrar tecnología retail.', 'obras-civiles'),
  ],
  '/ia/allcom-ia': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'IA', path: '/ia/allcom-ia' }, { name: 'ALLCOM IA Studio', path: '/ia/allcom-ia' }]),
    serviceSchema('ALLCOM IA Studio', 'Consultoría, agentes IA, prompts, automatización y prototipado rápido. Concepto en 48h.', 'allcom-ia'),
  ],
  '/ia/openclaw': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'IA', path: '/ia/allcom-ia' }, { name: 'OpenClaw / Cowork', path: '/ia/openclaw' }]),
    serviceSchema('OpenClaw / Claude Cowork', 'Agentes autónomos IA: data lakes con OpenClaw y productividad con Claude Cowork.', 'openclaw'),
  ],
  '/casos': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Casos de Éxito', path: '/casos' }]),
  ],
  '/empresa/nosotros': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Empresa', path: '/empresa/nosotros' }, { name: 'Nosotros', path: '/empresa/nosotros' }]),
  ],
  '/empresa/equipo': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Empresa', path: '/empresa/nosotros' }, { name: 'Equipo', path: '/empresa/equipo' }]),
  ],
  '/empresa/historia': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Empresa', path: '/empresa/nosotros' }, { name: 'Historia', path: '/empresa/historia' }]),
  ],
  '/empresa/partners': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Empresa', path: '/empresa/nosotros' }, { name: 'Partners', path: '/empresa/partners' }]),
  ],
  '/empresa/informacion-corporativa': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Empresa', path: '/empresa/nosotros' }, { name: 'Información Corporativa', path: '/empresa/informacion-corporativa' }]),
  ],
  '/contacto': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'Contacto', path: '/contacto' }]),
  ],
  '/ceocockpit': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'CEO Cockpit™', path: '/ceocockpit' }]),
  ],
  '/csuite': () => [
    breadcrumb([{ name: 'Inicio', path: '/' }, { name: 'C-Suite Dashboard™', path: '/csuite' }]),
  ],
};

// React component that injects schemas per route
export const PageSchema = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const generator = pageSchemas[pathname];
    if (generator) {
      injectSchema(generator());
    } else {
      // Generate breadcrumb from path
      const parts = pathname.split('/').filter(Boolean);
      const crumbs = [{ name: 'Inicio', path: '/' }];
      let path = '';
      parts.forEach(p => {
        path += `/${p}`;
        crumbs.push({ name: p.charAt(0).toUpperCase() + p.slice(1).replace(/-/g, ' '), path });
      });
      injectSchema([breadcrumb(crumbs)]);
    }
  }, [pathname]);

  return null;
};

// Export helpers for dynamic product pages
export { productSchema, faqSchema, breadcrumb, serviceSchema, injectSchema };
export default PageSchema;
